/* eslint-disable react-hooks/exhaustive-deps */
import { Badge, Button, Label } from '@/shared/ui'
import { X } from 'lucide-react'
import { useEffect, useState } from 'react'
import useRequest from '@/shared/lib/use-request'
import { UserCard, UserStore } from '@/entities/user'
import { IUser } from '@/entities/user/model/types'
import MemberModal from './modals/member-modal'
import { ContractsStore } from '@/shared/model/store/contracts-store'
import useNotifier from '@/shared/lib/use-notifier'
import { AlertModal } from '@/shared/ui/alert-component'
import { contractRead } from '@/shared/lib'
import LoadingIcon from '@/shared/ui/loading-icon'
import LimitsModal from './modals/limits-modal'
import { LangStore } from '@/shared/model/store'

type Props = {
	npo_address: `0x${string}`
}

const MembersNpo = ({ npo_address }: Props) => {
	const { userData } = UserStore()
	const { fetch, result, isLoading: isLoadingMembers } = useRequest<IUser[]>('npoMembers')
	const [admins, setAdmins] = useState<`0x${string}`[]>([])
	const { langPack } = LangStore()
	const { contracts } = ContractsStore()
	const { writeContract, isLoading } = useNotifier({
		onSuccess() {
			fetchML()
		}
	})

	const readIsAdmin = async () => {
		const res: `0x${string}`[] = await contractRead('SavvaNPO', 'getAdmins', [], npo_address)
		setAdmins(res)
	}

	const fetchML = () => {
		fetch({
			params: {
				npo_addr: npo_address
			}
		})
		readIsAdmin()
	}

	const isAdmin = userData?.address ? admins.includes(userData.address) : false
	const removeMember = (address: `0x${string}`) => {
		writeContract({
			abi: contracts.SavvaNPO.abi,
			address: npo_address,
			functionName: 'removeMember',
			args: [address]
		})
	}

	useEffect(() => {
		if (!npo_address) return
		fetchML()
	}, [npo_address])

	return (
		<div>
			<div className='mt-5 rounded-lg border-[1px] p-4 shadow'>
				<div className='mb-2 flex w-full items-center justify-between border-b-[1px] border-[#d1d5db] pb-2'>
					<Label className='flex h-8 items-center'>{langPack('npo.members')}</Label>
					{isAdmin && <MemberModal npo_address={npo_address} reload={fetchML} />}
				</div>

				<div className='flex w-full flex-col gap-4'>
					{isLoadingMembers ? (
						<LoadingIcon />
					) : result?.length ? (
						result.map((el: IUser) => (
							<div className='flex w-full items-center justify-between rounded-lg p-4 shadow-md'>
								<div className='flex w-[30%] items-center gap-4'>
									<UserCard user={el} />
									{admins.includes(el.address) && (
										<Badge variant={'orange'} className='h-[25px]'>
											{langPack('npo.admin')}
										</Badge>
									)}
								</div>
								<div className='flex justify-start gap-2'>
									{el.roles.map((role, index) => (
										<Badge key={index} variant={'secondary'}>
											{role}
										</Badge>
									))}
								</div>
								{isAdmin && (
									<div className='flex gap-4'>
										<LimitsModal user={el} npo_address={npo_address} />
										<MemberModal
											npo_address={npo_address}
											reload={fetchML}
											user={el}
											isAdmin={admins.includes(el.address)}
										/>
										<AlertModal
											title={langPack('npo.deleteButton')}
											description={langPack('npo.deleteConfirmation')}
											buttonTitle={
												<Button variant={'destructive'} size={'icon'} loading={isLoading}>
													<X size={12} />
												</Button>
											}
											onOk={() => removeMember(el.address)}
										/>
									</div>
								)}
							</div>
						))
					) : (
						<div className='text-[12px] text-[grey]'>{langPack('npo.emptyState')}</div>
					)}
				</div>
			</div>
		</div>
	)
}

export default MembersNpo
